import { formatCountdown } from './format-countdown'
import { UserFacingError } from './errors'

export class InvalidCredentialsError extends UserFacingError {
  constructor() {
    super('Invalid login credentials')
    this.name = 'InvalidCredentialsError'
  }
}

export class EmailNotConfirmedError extends UserFacingError {
  constructor() {
    super('Email not confirmed')
    this.name = 'EmailNotConfirmedError'
  }
}

export class NetworkError extends UserFacingError {
  constructor() {
    super('Network error')
    this.name = 'NetworkError'
  }
}

export class SessionExpiredError extends UserFacingError {
  constructor() {
    super('Your session expired. Please sign in again.')
    this.name = 'SessionExpiredError'
  }
}

export class RateLimitedError extends UserFacingError {
  constructor() {
    super('Too many requests')
    this.name = 'RateLimitedError'
  }
}

export const OTP_SEND_LIMIT_NO_EMAIL_MESSAGE =
  "You've requested too many codes for this number. Wait about 10 minutes and try again."

export class OtpSendLimitError extends UserFacingError {
  readonly retryAfterSeconds = 600
  readonly channel = 'phone' as const
  readonly messageWithoutAlternate = OTP_SEND_LIMIT_NO_EMAIL_MESSAGE
  constructor() {
    super(
      "You've requested too many codes for this number. Wait about 10 minutes, or use your email instead.",
    )
    this.name = 'OtpSendLimitError'
  }
}

const NO_SECONDS_THROTTLE_SECONDS = 300
const EMAIL_CAP_MESSAGE = 'Too many emails have been sent. Please wait about 5 minutes and try again.'
const PHONE_NO_SECONDS_MESSAGE = 'Please wait about 5 minutes before requesting another code.'

function emailThrottleMessage(noun: 'code' | 'link'): string {
  return `Check your inbox and spam for the ${noun} we already sent, or wait a few seconds and try again.`
}

export class OtpSendThrottledError extends UserFacingError {
  readonly channel: 'phone' | 'email'
  readonly retryAfterSeconds: number
  readonly linkMessage: string
  readonly messageWithoutAlternate: string
  constructor(channel: 'phone' | 'email', retryAfterSeconds?: number) {
    const hasSeconds =
      retryAfterSeconds !== undefined &&
      Number.isFinite(retryAfterSeconds) &&
      Number.isInteger(retryAfterSeconds) &&
      retryAfterSeconds > 0
    const emailMessage = hasSeconds ? emailThrottleMessage('code') : EMAIL_CAP_MESSAGE
    super(
      channel === 'email'
        ? emailMessage
        : hasSeconds
          ? `Please wait ${retryAfterSeconds} seconds before requesting another code.`
          : PHONE_NO_SECONDS_MESSAGE,
    )
    this.name = 'OtpSendThrottledError'
    this.channel = channel
    this.retryAfterSeconds = hasSeconds ? retryAfterSeconds : NO_SECONDS_THROTTLE_SECONDS
    this.linkMessage =
      channel === 'email' ? (hasSeconds ? emailThrottleMessage('link') : EMAIL_CAP_MESSAGE) : this.message
    this.messageWithoutAlternate = this.message
  }
}

export class ClaimSendLimitError extends UserFacingError {
  readonly channel: 'phone' | 'email'
  readonly retryAfterSeconds: number
  readonly messageWithoutAlternate: string
  constructor(channel: 'phone' | 'email', retryAfterSeconds: number) {
    const wait = formatCountdown(retryAfterSeconds)
    super(
      channel === 'phone'
        ? `You've requested too many codes for this number. Try again in ${wait}, or use your email instead.`
        : `You've requested too many codes for this email. Try again in ${wait}.`,
    )
    this.name = 'ClaimSendLimitError'
    this.channel = channel
    this.retryAfterSeconds = retryAfterSeconds
    this.messageWithoutAlternate =
      channel === 'phone'
        ? `You've requested too many codes for this number. Try again in ${wait}.`
        : this.message
  }
}

export class InviteRequestLimitError extends UserFacingError {
  readonly retryAfterSeconds: number
  readonly messageWithoutAlternate: string
  constructor(retryAfterSeconds: number) {
    super(`You've requested too many new invitations. Try again in ${formatCountdown(retryAfterSeconds)}.`)
    this.name = 'InviteRequestLimitError'
    this.retryAfterSeconds = retryAfterSeconds
    this.messageWithoutAlternate = this.message
  }
}

export class JoinSendLimitError extends UserFacingError {
  readonly retryAfterSeconds: number
  readonly messageWithoutAlternate: string
  constructor(retryAfterSeconds: number) {
    super(`Too many attempts. Try again in ${formatCountdown(retryAfterSeconds)}.`)
    this.name = 'JoinSendLimitError'
    this.retryAfterSeconds = retryAfterSeconds
    this.messageWithoutAlternate = this.message
  }
}

export class OtpSendFailedError extends UserFacingError {
  constructor() {
    super("We couldn't send your code. Please try again.")
    this.name = 'OtpSendFailedError'
  }
}

export type OtpSendRefusal =
  | OtpSendLimitError
  | OtpSendThrottledError
  | ClaimSendLimitError
  | InviteRequestLimitError
  | JoinSendLimitError

export function isOtpSendRefusal(err: unknown): err is OtpSendRefusal {
  return (
    err instanceof OtpSendLimitError ||
    err instanceof OtpSendThrottledError ||
    err instanceof ClaimSendLimitError ||
    err instanceof InviteRequestLimitError ||
    err instanceof JoinSendLimitError
  )
}

export function sendRefusalMessage(err: OtpSendRefusal, alternateChannelOffered: boolean): string {
  return alternateChannelOffered ? err.message : err.messageWithoutAlternate
}

export class InvalidOtpError extends UserFacingError {
  constructor() {
    super('Invalid verification code')
    this.name = 'InvalidOtpError'
  }
}

export class OtpExpiredError extends UserFacingError {
  constructor() {
    super('Verification code expired')
    this.name = 'OtpExpiredError'
  }
}

export class UserNotFoundError extends UserFacingError {
  constructor() {
    super('No account found')
    this.name = 'UserNotFoundError'
  }
}

export class EmailLinkedToDifferentAccountError extends UserFacingError {
  constructor() {
    super('This email is already linked to a different account. Contact support to merge them.')
    this.name = 'EmailLinkedToDifferentAccountError'
  }
}

export class PhoneLinkedToDifferentAccountError extends UserFacingError {
  constructor() {
    super(
      'This phone number is already linked to a different account. Contact support to merge them.',
    )
    this.name = 'PhoneLinkedToDifferentAccountError'
  }
}

export class IdentityMergeConflictError extends UserFacingError {
  constructor(message?: string) {
    super(
      message ??
        'This invitation belongs to an account that already exists under a different sign-in method. Please sign in with your original method.',
    )
    this.name = 'IdentityMergeConflictError'
  }
}

export const PLACEHOLDER_MERGE_LEDGER_CONFLICT_MESSAGE =
  "We couldn't combine your records with this organization's because both have an account balance there. Please contact support."

export class PlaceholderMergeLedgerConflictError extends UserFacingError {
  constructor() {
    super(PLACEHOLDER_MERGE_LEDGER_CONFLICT_MESSAGE)
    this.name = 'PlaceholderMergeLedgerConflictError'
  }
}

export class DuplicateContactError extends UserFacingError {
  constructor(message?: string) {
    super(
      message ??
        'The entered email/phone belongs to an existing account. Please use a different one.',
    )
    this.name = 'DuplicateContactError'
  }
}

export class ContactClaimRequiredError extends UserFacingError {
  readonly identifier: string
  readonly identifierType: 'email' | 'phone'
  readonly pendingInvite: boolean
  readonly holderUnregistered: boolean
  constructor(
    identifier: string,
    identifierType: 'email' | 'phone',
    pendingInvite = false,
    holderUnregistered = false,
    message?: string,
  ) {
    super(
      message ??
        (pendingInvite
          ? identifierType === 'email'
            ? 'This email has a pending invite — verify it to claim your profile.'
            : 'This phone number is on a profile with a pending invite — verify it to claim it.'
          : holderUnregistered
            ? identifierType === 'email'
              ? 'This email belongs to an unfinished signup. Verify it to claim it as yours.'
              : 'This phone number belongs to an unfinished signup. Verify it to claim it as yours.'
            : identifierType === 'email'
              ? 'This email is on another account but unverified — verify it to claim it.'
              : 'This phone number is on another account but unverified — verify it to claim it.'),
    )
    this.name = 'ContactClaimRequiredError'
    this.identifier = identifier
    this.identifierType = identifierType
    this.pendingInvite = pendingInvite
    this.holderUnregistered = holderUnregistered
  }
}

export class AccountExistsError extends UserFacingError {
  readonly identifier: string
  readonly identifierType: 'email' | 'phone'
  constructor(identifier: string, identifierType: 'email' | 'phone' = 'email', message?: string) {
    super(
      message ??
        (identifierType === 'email'
          ? 'This email is the sign-in address of an existing account — sign in to that account instead'
          : 'An account already exists with this phone number'),
    )
    this.name = 'AccountExistsError'
    this.identifier = identifier
    this.identifierType = identifierType
  }
}

export class IdentityDocumentUploadError extends UserFacingError {
  constructor(message?: string) {
    super(message ?? "We could not save your driver's licence. Please try again.")
    this.name = 'IdentityDocumentUploadError'
  }
}

export class ComplianceIncompleteError extends UserFacingError {
  readonly missingFields: string[]
  constructor(missingFields: string[], message?: string) {
    super(message ?? 'Please complete your profile before accepting the invitation.')
    this.name = 'ComplianceIncompleteError'
    this.missingFields = missingFields
  }
}
